import { Contact } from '@/types/contact';
import { ListResponse } from '@/types/common';
import {
  MarketSignalDateGroup,
  LinkedInSignalDateGroup,
  WebsiteSignalDateGroup,
} from '@/types/signals';
import { TargetList } from '@/types/targetList';

const API_BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || 'https://dev.app.1eye.ai';

export interface SalesforceSessionResponse {
  access_token: string;
  expires_at: number;
  client_id: string;
  contact_id: string;
}

export const exchangeSalesforceCode = async (code: string): Promise<SalesforceSessionResponse> => {
  const response = await fetch(`${API_BASE_URL}/salesforce_app/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Failed to authenticate Salesforce session';
    try {
      const parsed = JSON.parse(errorText);
      errorMessage = parsed?.error || parsed?.message || errorMessage;
    } catch {
      if (errorText) errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const getSalesforceContact = async (
  clientId: string,
  contactId: string,
  token: string
): Promise<Contact> => {
  const response = await fetch(`${API_BASE_URL}/salesforce_app/${clientId}/contact/${contactId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch contact details from Salesforce app');
  }

  return response.json();
};

export const getSalesforceWebsiteSignals = async ({
  clientId,
  contactId,
  token,
  startingAfter,
  limit,
  filters,
  match,
}: {
  clientId: string;
  contactId: string;
  token: string;
  startingAfter?: string;
  limit?: number;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
}): Promise<ListResponse<WebsiteSignalDateGroup>> => {
  const response = await fetch(
    `${API_BASE_URL}/salesforce_app/${clientId}/contact/${contactId}/website_signal/search`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...(startingAfter ? { starting_after: startingAfter } : {}),
        ...(limit ? { limit } : {}),
        ...(filters?.length ? { filter: { match: match || 'all', rules: filters } } : {}),
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch website signals from Salesforce app');
  }

  return response.json();
};

export const getSalesforceMarketSignals = async ({
  clientId,
  contactId,
  token,
  startingAfter,
  limit,
  filters,
  match,
}: {
  clientId: string;
  contactId: string;
  token: string;
  startingAfter?: string;
  limit?: number;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
}): Promise<ListResponse<MarketSignalDateGroup>> => {
  const response = await fetch(
    `${API_BASE_URL}/salesforce_app/${clientId}/contact/${contactId}/market_signal/search`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...(startingAfter ? { starting_after: startingAfter } : {}),
        ...(limit ? { limit } : {}),
        ...(filters?.length ? { filter: { match: match || 'all', rules: filters } } : {}),
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch market signals from Salesforce app');
  }

  return response.json();
};

export const getSalesforceLinkedInSignals = async ({
  clientId,
  contactId,
  token,
  startingAfter,
  limit,
  filters,
  match,
}: {
  clientId: string;
  contactId: string;
  token: string;
  startingAfter?: string;
  limit?: number;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
}): Promise<ListResponse<LinkedInSignalDateGroup>> => {
  const response = await fetch(
    `${API_BASE_URL}/salesforce_app/${clientId}/contact/${contactId}/linkedin_signal/search`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...(startingAfter ? { starting_after: startingAfter } : {}),
        ...(limit ? { limit } : {}),
        ...(filters?.length ? { filter: { match: match || 'all', rules: filters } } : {}),
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch LinkedIn signals from Salesforce app');
  }

  return response.json();
};

export const getSalesforceTargetLists = async ({
  clientId,
  contactId,
  token,
  limit,
  startingAfter,
}: {
  clientId: string;
  contactId: string;
  token: string;
  limit?: number;
  startingAfter?: string;
}): Promise<ListResponse<TargetList>> => {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());
  if (startingAfter) params.set('starting_after', startingAfter);

  const url = `${API_BASE_URL}/salesforce_app/${clientId}/contact/${contactId}/target_list${
    params.size ? `?${params.toString()}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch target lists from Salesforce app');
  }

  const raw = await response.json();
  const rawList: Array<TargetList & { overall_contacts_count?: number }> = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : [];

  const normalizedData = rawList.map(targetList => ({
    ...targetList,
    contact_count: targetList.overall_contacts_count ?? targetList.contact_count ?? 0,
  }));

  return {
    data: normalizedData,
    total: raw?.total ?? normalizedData.length,
    starting_after: raw?.starting_after || '',
    ending_before: raw?.ending_before || '',
    has_more: raw?.has_more ?? false,
  };
};
